import React, { createContext, ReactElement, useCallback, useEffect, useState } from 'react';
import { Validation, ValidationState } from '@vp/upload-and-print-validator';
import { AnalyticsEventData, sendEventTracking } from 'src/components/Analytics';
import { useProductContext } from 'src/hooks/ProductContextHooks';
import { useTrackingContext } from 'src/hooks/TrackingContextHooks';
import { useTrackingEvent } from 'src/hooks/useTrackingEvent';
import { UPLOAD_UNIFIED_EVENT, UploadSteps, ValidationNames } from 'src/utils/Constants';
import { text } from 'src/utils/localizationUtil';
import { createTrackingDataForProduct } from 'src/utils/trackingUtil';
import { getValidationsForProduct, processValidations } from 'src/utils/validationUtil';
import { usePromptContext } from './PromptContextProvider';

/**
 * These are list of different computed validations from the product validations
 */
const AllComputedValidations = ['coverPage'] as const;

interface ComputedValidationType {
  id: typeof AllComputedValidations[number];
  state: ValidationState;
}

export type PageDimensionValidationType = {
  page: number;
  actualWidth: number;
  actualHeight: number;
  expectedWidth: number;
  expectedHeight: number;
  minWidth: number;
  minHeight: number;
  maxWidth: number;
  maxHeight: number;
};

export interface ValidationContextProps {
  validations: Validation[] | null;
  validationPlaceholders: Validation[] | null;
  setValidations: (newValidations: Validation[] | null) => void;
  isValidating: boolean;
  setIsValidating: (isValidating: boolean) => void;
  setNeedsRevalidation: (needsRevalidation: boolean) => void;
  needsRevalidation: boolean;
  computedValidations: Array<ComputedValidationType>;
}

export const ValidationContext = createContext<ValidationContextProps>({
  validations: null,
  validationPlaceholders: null,
  setValidations: () => {
    return;
  },
  isValidating: false,
  setIsValidating: () => {
    return;
  },
  setNeedsRevalidation: () => {
    return;
  },
  needsRevalidation: true,
  computedValidations: [],
});

export interface ValidationContextProviderProps {
  children?: React.ReactNode;
}

export const ValidationContextProvider = ({ children }: ValidationContextProviderProps): ReactElement => {
  const productContext = useProductContext();
  const { trackingConfiguration } = useTrackingContext();
  const { setShowValidationErrorPrompt } = usePromptContext();
  const { sendSegmentTrackingEvent } = useTrackingEvent();

  const [validationPlaceholders, setValidationPlaceholders] = useState<Validation[] | null>(null);
  const [validations, setValidationsInternal] = useState<Validation[] | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [needsRevalidation, setNeedsRevalidation] = useState(true);
  const [computedValidations, setComputedValidations] = useState<Array<ComputedValidationType>>([
    { id: 'coverPage', state: ValidationState.UnValidated },
  ]);

  const trackingDataForProduct = createTrackingDataForProduct(productContext.productInformation);

  /**
   * Checks if the provided error data indicates a cover page error.
   *
   * A cover page error is detected when there are errors on pages up to and including page 2,
   * but no errors on pages greater than 2.
   *
   * @param errorData - An array of error objects, where each object has a `page` property indicating the page number.
   * @returns `true` if a cover page error is detected, `false` otherwise.
   */
  const checkIsCoverPageError = useCallback((errorData: Array<PageDimensionValidationType>) => {
    if (errorData.length <= 2) {
      return errorData.some(error => error.page <= 2);
    }
    return false;
  }, []);

  const processTemplateMismatchValidation = useCallback(
    (processedValidations: Validation[]) => {
      processedValidations?.forEach(({ id, details, state }) => {
        if (id === ValidationNames.PageSizeValidator) {
          if (state === ValidationState.Error) {
            const errorData: Array<PageDimensionValidationType> = details?.errors;
            if (errorData && errorData.length > 0) {
              const isCoverPageError = checkIsCoverPageError(errorData);
              setComputedValidations([
                { id: 'coverPage', state: isCoverPageError ? ValidationState.Error : ValidationState.Ok },
              ]);
              isCoverPageError &&
                sendSegmentTrackingEvent({
                  action: UPLOAD_UNIFIED_EVENT,
                  uploadStep: UploadSteps.ValidationFailed,
                  category: 'Design - Upload and Print',
                  label: 'Template Mismatch Error',
                } as AnalyticsEventData);
            }
          }
        }
      });
    },
    [checkIsCoverPageError, sendSegmentTrackingEvent]
  );

  const setValidations = useCallback(
    (newValidations: Validation[] | null) => {
      const processedValidations = newValidations
        ? processValidations(newValidations, productContext.productOptionTranslations)
        : newValidations;
      setValidationsInternal(processedValidations);

      const unvalidatedValidationsExist =
        processedValidations?.some(validation => validation.state === ValidationState.UnValidated) ?? false;

      processedValidations?.forEach(validation => {
        if (validation.state === 'Error' || validation.state === 'Warning') {
          sendEventTracking({
            ...trackingConfiguration,
            //Not spreading trackingDataForProduct here because that would cause this useCallback to have a dependency on trackingDataForProduct,
            //which would be a new object every update, causing this useCallback to be recalculated every update, even if the actual
            //properties did not change
            productId: trackingDataForProduct.product_id,
            name: trackingDataForProduct.name,
            core_product_id: trackingDataForProduct.core_product_id,
            core_product_version: trackingDataForProduct.core_product_version,
            action: UPLOAD_UNIFIED_EVENT,
            uploadStep: validation.state === 'Warning' ? UploadSteps.ValidationWarning : UploadSteps.ValidationFailed,
            category: 'Design - Upload and Print',
            errorMessage: text(validation.message, validation.tokenValues),
            label: validation.id,
          } as AnalyticsEventData);
        }
      });

      processedValidations && processTemplateMismatchValidation(processedValidations);

      setNeedsRevalidation(unvalidatedValidationsExist);

      processedValidations?.filter(validation => {
        if (validation.state === 'Error') {
          setShowValidationErrorPrompt(true);
        }
      });
    },
    [
      productContext.productOptionTranslations,
      processTemplateMismatchValidation,
      trackingConfiguration,
      trackingDataForProduct.product_id,
      trackingDataForProduct.name,
      trackingDataForProduct.core_product_id,
      trackingDataForProduct.core_product_version,
      setShowValidationErrorPrompt,
    ]
  );

  useEffect(() => {
    let isStale = false;

    async function getValidations() {
      if (productContext.isProductReady) {
        const requiredValidations = await getValidationsForProduct(productContext.productInformation);
        if (isStale) {
          return;
        }

        const processedValidations = processValidations(requiredValidations, productContext.productOptionTranslations);
        setValidationPlaceholders(processedValidations);
      }
    }

    getValidations();

    return () => {
      isStale = true;
    };
  }, [productContext]);

  return (
    <ValidationContext.Provider
      value={{
        validations: validations,
        validationPlaceholders: validationPlaceholders,
        setValidations,
        isValidating,
        setIsValidating,
        setNeedsRevalidation,
        needsRevalidation,
        computedValidations,
      }}
    >
      {children}
    </ValidationContext.Provider>
  );
};
